import { PlayerProfile, UpgradeJob, UpgradeTarget } from "./profile";
import { goldFromLegacy, isLegacyResources } from "./gold";

/**
 * The free upgrade queue (FINISH_PLAN D4, DESIGN §8.1): each builder holds `commerce.queuePerBuilder` paid next
 * upgrades. A queued upgrade is paid when queued and starts by itself the moment a builder is free and its target is
 * not being upgraded — at the end time of the job that freed them, so the queue keeps working while the game is
 * closed (PlayerProfile.claimCompleted). With no push notifications on the web, it replaces "come back at the exact
 * minute". Never sold: queue slots come with builders.
 */

export interface QueuedUpgrade {
  /** Also the job's id once it starts. */
  readonly id: number;
  readonly target: UpgradeTarget;
  readonly towerIndex: number;
  readonly toLevel: number;
  /** Gold paid when queued; paid back in full if it is cancelled before it starts. */
  readonly cost: number;
  readonly queuedAt: number;
}

const SECONDS_PER_MINUTE = 60;

const onTarget = (q: { target: UpgradeTarget; towerIndex: number }, target: UpgradeTarget, towerIndex: number): boolean =>
  q.target === target && (target === UpgradeTarget.Base || q.towerIndex === towerIndex);

/** Queue slots: `queuePerBuilder` per builder, plus the VIP perk's extra per builder (docs/SHOP.md §2). */
export function queueCapacity(p: PlayerProfile): number { return p.buildersOwned * (p.balance.commerce.queuePerBuilder + p.vip.extraQueuePerBuilder); }
export function isQueued(p: PlayerProfile, target: UpgradeTarget, towerIndex: number): boolean { return p.queue.some((q) => onTarget(q, target, towerIndex)); }

/** The level a target's next upgrade would reach, counting its running job and what is queued on it. */
export function nextUpgradeLevel(p: PlayerProfile, target: UpgradeTarget, towerIndex: number): number {
  const running = p.isUpgrading(target, towerIndex) ? 1 : 0;
  return p.levelOf(target, towerIndex) + running + p.queue.filter((q) => onTarget(q, target, towerIndex)).length + 1;
}

/**
 * Queues the next upgrade of a target (D4): only when it cannot start now — every builder is busy, or the target is
 * already being upgraded or queued (then it is the level after that). Pays its cost now.
 */
export function queueUpgrade(p: PlayerProfile, target: UpgradeTarget, towerIndex: number, now: number): QueuedUpgrade | string {
  if (target === UpgradeTarget.Tower && (towerIndex < 0 || towerIndex >= p.towerLevels.length || p.balance.towers[towerIndex].isBase)) return "no such tower";
  if (target === UpgradeTarget.Pet && p.levelOf(target, towerIndex) === 0) return "no such pet";
  if (p.queue.length >= queueCapacity(p)) return "queue full";
  const busy = p.isUpgrading(target, towerIndex) || isQueued(p, target, towerIndex);
  if (!busy && p.freeBuilders > 0) return "a builder is free";
  const toLevel = nextUpgradeLevel(p, target, towerIndex);
  if (toLevel > p.balance.tables.maxLevel) return "max level";
  if (toLevel > p.upgradeCap(target, towerIndex)) return "level cap";
  const cost = p.costFor(toLevel);
  if (!p.canAfford(cost)) return "not enough gold";
  p.gold -= cost;
  const q: QueuedUpgrade = { id: p.allocateJobId(), target, towerIndex: target === UpgradeTarget.Base ? 0 : towerIndex, toLevel, cost, queuedAt: now };
  p.queue.push(q);
  return q;
}

/**
 * Cancels a queued upgrade, and every later one queued on the same target (each builds on the level before it). All
 * are paid back in full: nothing was built. Returns the refund, or null when no such entry is queued.
 */
export function cancelQueued(p: PlayerProfile, id: number): number | null {
  const q = p.queue.find((x) => x.id === id);
  if (!q) return null;
  let refund = 0;
  for (let i = p.queue.length - 1; i >= 0; i--) {
    const x = p.queue[i];
    if (!onTarget(x, q.target, q.towerIndex) || x.toLevel < q.toLevel) continue;
    refund += x.cost;
    p.queue.splice(i, 1);
  }
  p.addGold(refund);
  return refund;
}

/**
 * Starts, in queue order, every queued upgrade a free builder and a free target allow, at `at` (never before it was
 * queued). One whose level no longer follows its target's (the level moved some other way) is dropped and paid back.
 */
export function startQueued(p: PlayerProfile, at: number): UpgradeJob[] {
  const started: UpgradeJob[] = [];
  for (let i = 0; i < p.queue.length && p.freeBuilders > 0;) {
    const q = p.queue[i];
    if (p.isUpgrading(q.target, q.towerIndex)) { i++; continue; }
    p.queue.splice(i, 1);
    if (q.toLevel !== p.levelOf(q.target, q.towerIndex) + 1) { p.addGold(q.cost); continue; }
    const startedAt = Math.max(at, q.queuedAt), duration = p.durationFor(q.toLevel);
    const job: UpgradeJob = { id: q.id, target: q.target, towerIndex: q.towerIndex, toLevel: q.toLevel, startedAt, endAt: startedAt + duration, durationSeconds: duration };
    p.jobs.push(job);
    started.push(job);
  }
  return started;
}

/** Whether a running upgrade can be finished free now (VIP `freeFinishMinutes`, docs/SHOP.md §2): true, or why not. */
export function canFinishFree(p: PlayerProfile, jobId: number, now: number): true | string {
  const job = p.jobs.find((j) => j.id === jobId);
  if (!job) return "no such job";
  const left = job.endAt - now;
  if (left <= 0) return "already complete";
  if (left > p.vip.freeFinishMinutes * SECONDS_PER_MINUTE) return "too much time left";
  return true;
}

/** Finishes a running upgrade free (VIP): its end moves to `now`; the caller claims it (PlayerProfile.claimCompleted). */
export function finishFree(p: PlayerProfile, jobId: number, now: number): true | string {
  const ok = canFinishFree(p, jobId, now);
  if (ok !== true) return ok;
  p.jobs.find((j) => j.id === jobId)!.endAt = now;
  return true;
}

/**
 * Queued upgrades from a save; malformed entries are dropped (their gold is lost with the entry, as before the queue).
 * A pre-gold entry's {wood, iron, crystal} cost converts to gold (meta/gold.ts), so a cancel pays back what it is worth.
 */
export function parseQueued(v: unknown): QueuedUpgrade[] {
  if (!Array.isArray(v)) return [];
  const ok = (n: unknown) => Number.isInteger(n) && (n as number) >= 0;
  return v.map((q) => (typeof q === "object" && q !== null && isLegacyResources(q.cost) ? { ...q, cost: goldFromLegacy(q.cost) } : q))
    .filter((q): q is QueuedUpgrade => typeof q === "object" && q !== null && ok(q.id) && (q.target === UpgradeTarget.Tower || q.target === UpgradeTarget.Base || q.target === UpgradeTarget.Pet) && ok(q.towerIndex)
    && ok(q.toLevel) && Number.isFinite(q.queuedAt) && ok(q.cost))
    .map((q) => ({ ...q }));
}
